import { useEffect, useRef, type RefObject } from 'react';

/**
 * Sajat popover bezarasa Escape-re es melle kattintasra.
 *
 * A Radix-os popoverek ezt maguktol tudjak - a kezzel rakott panelek (meghivo,
 * atadas, tarcsazo, megosztas) viszont csak a sajat gombjukkal zarodtak, es
 * billentyuzettel egyaltalan nem lehetett kilepni beloluk.
 *
 * A `ref` a panel gyokere: ami azon BELUL tortenik, az nem zar. A panelt nyito
 * gombot is erdemes a `ref` ala tenni (vagy `ignore`-ba), kulonben a gombra
 * kattintas eloszor bezar, aztan a gomb onClick-je azonnal ujra kinyitja.
 *
 * Az Escape a FOKUSZT visszaadja a nyito gombnak, ha az meg van (`returnFocus`),
 * hogy a billentyuzetes hasznalo ne a lap elejere essen vissza.
 */
export function useDismiss(
  ref: RefObject<HTMLElement | null>,
  onDismiss: () => void,
  options: {
    enabled?: boolean;
    /** Ezekre kattintva sem zar (pl. a nyito gomb, ha a panelen kivul all). */
    ignore?: RefObject<HTMLElement | null>[];
    /** Escape utan ide kerul a fokusz. */
    returnFocus?: RefObject<HTMLElement | null>;
  } = {},
): void {
  const { enabled = true } = options;
  // A legfrissebb visszahivas es beallitas - igy a figyelok nem iratkoznak
  // fel ujra minden rendereleskor.
  const latest = useRef({ onDismiss, options });
  latest.current = { onDismiss, options };

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Egy nyitott Radix-parbeszed a sajat Escape-jet kezeli - az ne zarja
      // a mogotte levo panelt is.
      if (event.defaultPrevented) return;
      latest.current.onDismiss();
      latest.current.options.returnFocus?.current?.focus();
    };

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (ref.current?.contains(target)) return;
      if (latest.current.options.ignore?.some((r) => r.current?.contains(target))) return;
      latest.current.onDismiss();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [enabled, ref]);
}
